import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Snowflake, Clock, Leaf, ArrowRight, CheckCircle, Thermometer, Package, MapPin, Truck, Zap, Droplets, Settings, FileText, ArrowDown, TrendingUp, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import UnifiedContactModal from '../components/UnifiedContactModal';

const PortableHydrocoolersPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const heroBullets = [
    'Remove 40°F / ~22°C of field heat in minutes instead of hours',
    'Meet time-to-cool targets even at remote ranches and field collection centers'
  ];

  const timeTargets = [
    'Remove field heat within 2–4 hours of harvest wherever possible',
    'Cool pulp temperature below about 41°F / 5°C within that window',
    'Finish cooling toward 32–34°F / 0–1°C before long storage or export',
    'Maintain high humidity in holding and staging areas to reduce water loss and stem shrivel'
  ];

  const scenarios = [
    {
      icon: MapPin,
      title: 'Remote Ranches and Long Hauls',
      points: [
        'Place portable hydrocoolers at ranches or field collection centers',
        'Cool fruit from around 80°F / 27°C down into the 30s°F / low single digits °C before transport',
        'Reduce the cooling load and risk when fruit finally reaches the plant'
      ]
    },
    {
      icon: Thermometer,
      title: 'Hot Midday Harvests',
      points: [
        'Set portable hydrocoolers in shaded yards to strip out field heat from bins picked during the hottest part of the day',
        'Protect stem and fruit quality when temperature and vapor pressure deficit are highest'
      ]
    },
    {
      icon: Truck,
      title: 'Export and Long Storage Programs',
      points: [
        'Use portable hydrocooling as the first deep cooling step so export fruit hits storage just above its freezing point quickly',
        'Help extend shelf life and reduce rejections at distant or premium markets'
      ]
    },
    {
      icon: Package,
      title: 'Mixed Varieties and Special Lots',
      points: [
        'Use batch-style portable hydrocoolers with multiple zones to handle small, high-value lots without disrupting main tunnels',
        'Offer tailored handling for organic programs, trial blocks, or key customers'
      ]
    }
  ];

  const coldChainSteps = [
    {
      number: '1',
      title: 'Harvest in the Orchard',
      description: 'Fruit is picked and moved to nearby shaded yards or collection centers'
    },
    {
      number: '2',
      title: 'Portable Hydrocooler at Ranch or Yard',
      description: 'Portable tunnel or batch hydrocooler removes field heat within the first few hours',
      highlight: true
    },
    {
      number: '3',
      title: 'Haul to Packhouse',
      description: 'Fruit travels already cooled into the 30s°F / low single digits °C, relieving pressure on packhouse tunnels'
    },
    {
      number: '4',
      title: 'Packhouse Hydrocooling & Cold Storage',
      description: 'Stationary hydrocoolers trim temperature to final targets; cold rooms hold fruit near 32–34°F / 0–1°C'
    },
    {
      number: '5',
      title: 'Transport to Market',
      description: 'A stable cold chain maintains quality through shipping and retail'
    }
  ];

  const faqs = [
    {
      question: 'Can the same portable hydrocooler be used at the ranch and at the packhouse?',
      answer: 'Yes. Many units are configured for field-side use during harvest and then parked as part of packhouse flow the rest of the season. This gives you maximum flexibility and return on investment across different phases of your operation.'
    },
    {
      question: 'What power and utilities do portable hydrocoolers need?',
      answer: 'Requirements depend on size and refrigeration design. CTC works with you and your refrigeration contractor to define electrical service, water supply, and drainage needs based on your specific configuration and site conditions.'
    },
    {
      question: 'How do I know if I need a tunnel or batch portable hydrocooler?',
      answer: 'High, steady volumes point to tunnel configurations, while varied or smaller lots lean toward batch systems. Talk with CTC to review your bins per hour, variety mix, and layout - we\'ll help you match the right configuration to your operation.'
    },
    {
      question: 'Can portable hydrocoolers work for crops other than cherries?',
      answer: 'Yes. Portable hydrocoolers are also used for other stone fruit, berries, grapes, and other heat-sensitive crops. Cooling settings and cycle times are tailored crop-by-crop to hit the right temperature targets without damaging delicate fruit.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Portable Hydrocoolers | Field-Side Cooling for Cherries and Other Crops"
        description="Portable tunnel and batch hydrocoolers that start the cold chain at the ranch. Remove field heat within hours of harvest for cherries and other heat-sensitive crops."
        canonicalPath="/portable-hydrocoolers"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen md:min-h-[70vh] flex items-center justify-center overflow-hidden pt-16 pb-8">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="/Portable.jpg"
            alt="Portable hydrocooler at field site"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/85 via-cyan-800/75 to-blue-950/70"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center py-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-4 md:mb-6"
            >
              <Truck size={18} />
              <span>Portable Hydrocooling Solutions</span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight">
              Portable Hydrocoolers
              <span className="block text-cyan-300">for the First Miles of Your Cold Chain</span>
            </h1>

            <p className="text-lg md:text-xl lg:text-2xl mb-6 text-white/90 leading-relaxed max-w-4xl mx-auto">
              Portable tunnel and batch hydrocoolers that pull out field heat fast at the ranch or field yard, so your fruit reaches cold storage at the right temperature on time.
            </p>

            <div className="max-w-3xl mx-auto mb-8">
              {heroBullets.map((bullet, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-start space-x-3 mb-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3"
                >
                  <CheckCircle className="text-cyan-300 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-white/95 text-left">{bullet}</span>
                </motion.div>
              ))}
            </div>

            <motion.button
              onClick={() => setIsContactModalOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 inline-flex items-center space-x-3 shadow-xl"
            >
              <span>Plan a Portable Hydrocooling System</span>
              <ArrowRight size={20} className="md:w-6 md:h-6" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Why Portable Hydrocooling Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Why Portable Hydrocooling?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="space-y-4">
                <p className="text-lg text-gray-700 leading-relaxed">
                  Every cherry and fruit operation talks about "the cold chain," but the real driver of quality is <strong>how quickly fruit first gets cold after harvest</strong>.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  The problem is the <strong className="text-blue-600">"hot gap"</strong>: the 2–8 hours that can pass between picking and first cooling when fruit sits warm in the field, at collection centers, or on trucks. During this time, respiration accelerates, firmness drops, and shelf life shortens—before the fruit even reaches the packhouse.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Portable hydrocoolers move the first deep cooling step closer to where fruit is picked, so the cold chain truly starts at the ranch instead of only at the packhouse. By removing field heat within the first few hours, you protect quality from the beginning and set up every downstream step for success.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="/Website copy.jpg"
                alt="Portable hydrocooler units in field yard for cherry cooling"
                className="rounded-2xl shadow-2xl w-full"
              />
            </motion.div>
          </div>

          {/* Time and Temperature Targets Callout */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-cyan-50 to-blue-100 rounded-2xl p-8 border-2 border-blue-300 shadow-xl"
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
                <Thermometer className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Time and Temperature Targets for Heat-Sensitive Crops
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {timeTargets.map((target, index) => (
                <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-800 font-medium">{target}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Where Portable Hydrocooling Fits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Where Portable Hydrocooling Fits in Your Operation
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Portable hydrocoolers address real operational challenges across different harvest and handling scenarios
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {scenarios.map((scenario, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <scenario.icon className="text-white" size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                </div>
                <ul className="space-y-3">
                  {scenario.points.map((point, idx) => (
                    <li key={idx} className="flex items-start space-x-2">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700">{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tunnel or Batch Comparison Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Portable Hydrocoolers: Tunnel or Batch, Depending on Your Flow
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              CTC builds both tunnel and batch hydrocoolers, and either can be configured as portable units on skids or trailers. The choice is about throughput and flexibility, not whether it can be portable.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Portable Tunnel */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-xl p-8 shadow-lg border border-blue-200"
            >
              <div className="mb-6">
                <img
                  src="/img.jpg"
                  alt="Portable tunnel hydrocooler"
                  className="rounded-lg w-full h-64 object-cover"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Portable Tunnel Hydrocoolers
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Conveyorized, high-throughput hydrocooling with bins moving steadily through the tunnel</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Ideal for large ranches, centralized field yards, or operations with steady bin traffic</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Can be deployed as field-side units during harvest and then tied into the main packhouse system the rest of the season</span>
                </li>
              </ul>
            </motion.div>

            {/* Portable Batch */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-xl p-8 shadow-lg border border-blue-200"
            >
              <div className="mb-6">
                <img
                  src="/Batch Model.jpg"
                  alt="Portable batch hydrocooler"
                  className="rounded-lg w-full h-64 object-cover"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Portable Batch Hydrocoolers
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Zoned batch units that cool stacks of bins at a time</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Individually controlled zones to run different varieties, blocks, or customers in parallel</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Lower capital cost than a full conveyor tunnel and highly flexible for mixed lots, smaller sites, and trial programs</span>
                </li>
              </ul>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center bg-white rounded-xl p-6 border border-blue-200 shadow-lg"
          >
            <p className="text-lg text-gray-700">
              The same designs are also available as stationary packhouse hydrocoolers when portability is not required. For a full overview, see our{' '}
              <Link to="/hydrocoolers" className="text-blue-600 hover:text-blue-700 font-medium underline">
                Hydrocoolers
              </Link>{' '}
              page.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Cold Chain Flow Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              How Portable Hydrocooling Fits in the Cold Chain
            </h2>
          </motion.div>

          <div className="relative mb-12">
            {/* Connecting Line */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200 transform -translate-y-1/2 hidden md:block"></div>

            {/* Steps */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {coldChainSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`relative ${step.highlight ? 'z-10' : ''}`}
                >
                  <div className={`
                    ${step.highlight
                      ? 'bg-gradient-to-br from-blue-500 to-cyan-600 text-white shadow-lg transform md:-translate-y-4'
                      : 'bg-white text-gray-800 border border-gray-200'}
                    rounded-xl p-6 h-full transition-all duration-300 hover:shadow-md relative
                  `}>
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold mb-4 mx-auto
                      ${step.highlight
                        ? 'bg-white text-blue-600'
                        : 'bg-blue-100 text-blue-600'}
                    `}>
                      {step.number}
                    </div>
                    <h3 className={`text-lg font-semibold mb-3 text-center ${step.highlight ? 'text-white' : 'text-gray-900'}`}>
                      {step.title}
                    </h3>
                    <p className={`text-center text-sm ${step.highlight ? 'text-white/90' : 'text-gray-600'}`}>
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-blue-50 rounded-xl p-6 border border-blue-200 max-w-4xl mx-auto"
          >
            <p className="text-lg text-gray-700 text-center">
              Portable hydrocooling doesn't replace packhouse hydrocoolers; it sets them up for success by bringing fruit into the system already cold. This reduces the load on central cooling infrastructure and improves quality across the board.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Water Filtration Section */}
      <section className="py-16 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
              <Droplets className="text-white" size={32} />
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Filtration and Water-Treatment Options
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="space-y-4">
                <p className="text-lg text-gray-700 leading-relaxed">
                  Portable hydrocoolers often run on different water sources—well, canal, or municipal—and maintaining clean water is important for cooling performance and equipment life.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  CTC can integrate filtration and treatment options into portable units. We work with your team and food safety team to design systems that fit your water quality and operational requirements.
                </p>

                <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm mt-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Common Integration Options:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700">Inline filtration to protect spray nozzles and keep heat-transfer surfaces clean</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700">Options for sand, screen, or pressure filters depending on water quality</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700">Integration of additional treatment steps (such as UV units or chemical dosing) where customers require them, in coordination with their food safety team</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="/img_7073.jpeg"
                alt="Water filtration system"
                className="rounded-2xl shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Design Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
              <Settings className="text-white" size={32} />
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              How We Design a Portable Hydrocooling Plan
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every ranch and packing program is different, so we start with your actual harvest and haul realities.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[
              {
                icon: TrendingUp,
                title: 'Peak bins per hour or tons per hour from each ranch or block'
              },
              {
                icon: Thermometer,
                title: 'Typical and worst-case temperatures during picking and hauling'
              },
              {
                icon: MapPin,
                title: 'Distance and travel time between ranches, collection centers, and the packhouse'
              },
              {
                icon: Package,
                title: 'Varieties, programs, and lots that need special handling'
              },
              {
                icon: Truck,
                title: 'Where it makes the most sense to park a tunnel vs a batch unit'
              },
              {
                icon: Settings,
                title: 'Whether units need to move during the season'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200"
              >
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="text-white" size={20} />
                  </div>
                  <p className="text-gray-700 font-medium">{item.title}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-xl p-8 shadow-lg border border-blue-200 text-center"
          >
            <p className="text-lg text-gray-700">
              From there, we propose a portable hydrocooler configuration and work with your refrigeration partner to hit your time-to-cool targets.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Trusted by Leading PNW Cherry Growers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              For decades, the region's top cherry producers have relied on CTC Waterworks hydrocoolers to maximize quality and shelf life.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            <motion.a
              href="https://washfruitgrowers.com/"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
              className="bg-white rounded-xl p-6 shadow-md border border-gray-200 flex items-center justify-center h-32"
            >
              <img
                src="/dealer-logos/washington-fruit.png"
                alt="Washington Fruit"
                className="max-h-full max-w-full object-contain"
                loading="eager"
                onError={(e) => {
                  e.currentTarget.parentElement!.innerHTML = '<div class="font-bold text-lg text-red-600">WASHINGTON FRUIT</div>';
                }}
              />
            </motion.a>

            <motion.a
              href="https://rainierfruit.com/"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              whileHover={{ scale: 1.05 }}
              className="bg-white rounded-xl p-6 shadow-md border border-gray-200 flex items-center justify-center h-32"
            >
              <img
                src="/dealer-logos/rainier-fruit.png"
                alt="Rainier Fruit"
                className="max-h-full max-w-full object-contain"
                loading="eager"
                onError={(e) => {
                  e.currentTarget.parentElement!.innerHTML = '<div class="font-bold text-lg text-red-600">RAINIER FRUIT</div>';
                }}
              />
            </motion.a>

            <motion.a
              href="https://allanbrosfruit.com/"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ scale: 1.05 }}
              className="bg-white rounded-xl p-6 shadow-md border border-gray-200 flex items-center justify-center h-32"
            >
              <img
                src="/dealer-logos/allan-bros.png"
                alt="Allan Brothers"
                className="max-h-full max-w-full object-contain"
                loading="eager"
                onError={(e) => {
                  e.currentTarget.parentElement!.innerHTML = '<div class="font-bold text-lg text-red-600">ALLAN BROTHERS</div>';
                }}
              />
            </motion.a>

            <motion.a
              href="https://gebbersfarms.com/"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              whileHover={{ scale: 1.05 }}
              className="bg-white rounded-xl p-6 shadow-md border border-gray-200 flex items-center justify-center h-32"
            >
              <img
                src="/dealer-logos/gebbers-farms.png"
                alt="Gebbers Farms"
                className="max-h-full max-w-full object-contain"
                loading="eager"
              />
            </motion.a>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-xl p-8 shadow-lg border border-blue-200 mb-12"
          >
            <h3 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
              Refrigeration Partners
            </h3>
            <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
              We work closely with these trusted refrigeration companies, but are happy to collaborate with any refrigeration provider you prefer.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <motion.a
                href="https://versatilerefrigeration.com/"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200 flex items-center justify-center h-24"
              >
                <img
                  src="/dealer-logos/versatile-refrigeration.jpg"
                  alt="Versatile Refrigeration"
                  className="max-h-full max-w-full object-contain"
                  loading="eager"
                  onError={(e) => {
                    e.currentTarget.parentElement!.innerHTML = '<div class="font-bold text-lg text-blue-600">VERSATILE REFRIGERATION</div>';
                  }}
                />
              </motion.a>

              <motion.a
                href="https://www.cwrcentral.com/"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200 flex items-center justify-center h-24"
              >
                <img
                  src="/dealer-logos/cwr.png"
                  alt="Central Washington Refrigeration"
                  className="max-h-full max-w-full object-contain"
                  loading="eager"
                  onError={(e) => {
                    e.currentTarget.parentElement!.innerHTML = '<div class="font-bold text-lg text-blue-600">CENTRAL WASHINGTON REFRIGERATION</div>';
                  }}
                />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Learn More and Next Steps Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-cyan-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Take Minutes Out of the Gap Between Harvest and First Cooling
            </h2>
            <p className="text-xl text-white/90 mb-4">
              Removing field heat quickly in the first 2–4 hours often has more impact on cherry quality than extra days of storage.
            </p>
            <p className="text-xl text-white/90 mb-8">
              Portable hydrocoolers let growers start their cold chain at the ranch instead of only at the packhouse, protecting quality from the moment fruit is picked.
            </p>

            <motion.button
              onClick={() => setIsContactModalOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-blue-600 px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-gray-100 transition-all duration-300 inline-flex items-center space-x-3 shadow-xl mb-8"
            >
              <span>Talk About a Portable Hydrocooler</span>
              <ArrowRight size={20} className="md:w-6 md:h-6" />
            </motion.button>

            <div className="space-y-3 text-white/90">
              <p className="text-lg">
                Want the technical deep dive? Read our article:{' '}
                <Link to="/blog/minutes-matter-why-early-hydrocooling-makes-or-breaks-cherry-quality" className="text-cyan-200 hover:text-cyan-100 font-medium underline">
                  Minutes Matter: Why Early Hydrocooling Makes or Breaks Cherry Quality
                </Link>
              </p>
              <p className="text-lg">
                For a full overview of all hydrocooler configurations, visit our{' '}
                <Link to="/hydrocoolers" className="text-cyan-200 hover:text-cyan-100 font-medium underline">
                  Hydrocoolers
                </Link>
                {' '}and{' '}
                <Link to="/cherry-hydrocoolers" className="text-cyan-200 hover:text-cyan-100 font-medium underline">
                  Cherry Hydrocoolers
                </Link>
                {' '}pages.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        formType="quote"
        productContext="portable-hydrocoolers"
      />
    </div>
  );
};

export default PortableHydrocoolersPage;
